import { useCallback, useState, type ChangeEvent } from "react";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import type { Glider } from "../lib/glider";
import { filterGliders } from "../lib/glider-db";
import { useConfiguration } from "../hooks/useConfiguration";

type GliderPickerProps = {
  readonly current: Glider;
  readonly gliders: readonly Glider[] | null;
  readonly onOpen: () => void;
};

const displayValue = (glider: Glider | null): string =>
  glider?.displayName ?? "";

/** Searchable combobox over the full database. */
export function GliderPicker({ current, gliders, onOpen }: GliderPickerProps) {
  const { dispatch } = useConfiguration();
  const [query, setQuery] = useState("");

  const onChange = useCallback(
    (glider: Glider | null) => {
      if (glider !== null) dispatch({ type: "setGlider", gliderId: glider.id });
    },
    [dispatch],
  );

  const onInput = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setQuery(e.target.value),
    [],
  );

  const matches = gliders !== null ? filterGliders(gliders, query) : [];

  return (
    <div className="mb-5">
      <Combobox value={current} onChange={onChange} by="id">
        <label
          htmlFor="glider-picker"
          className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-muted"
        >
          Glider
        </label>
        <div className="relative">
          <ComboboxInput
            id="glider-picker"
            aria-label="Glider"
            autoComplete="off"
            displayValue={displayValue}
            onChange={onInput}
            onFocus={onOpen}
            placeholder="Search gliders…"
            className="min-h-11 w-full rounded-md border border-border bg-panel-2 px-2.5 py-2 pr-9 text-[13px] text-ink placeholder:text-muted focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2 md:min-h-0"
          />
          <ComboboxButton
            aria-label="Show gliders"
            onClick={onOpen}
            className="absolute inset-y-0 right-0 grid w-9 place-items-center text-muted hover:text-ink focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2"
          >
            <span aria-hidden="true">▾</span>
          </ComboboxButton>
          <ComboboxOptions
            className="absolute z-10 mt-1 max-h-72 w-full overflow-auto rounded-md border border-border bg-panel-2 py-1 shadow-lg empty:hidden"
          >
            {matches.map((glider) => (
              <ComboboxOption
                key={glider.id}
                value={glider}
                className="cursor-pointer px-3 py-2 text-[13px] text-ink data-[focus]:bg-accent data-[focus]:text-canvas data-[selected]:font-semibold"
              >
                {glider.displayName}
              </ComboboxOption>
            ))}
            {gliders !== null && matches.length === 0 && (
              <div className="px-3 py-2 text-[13px] text-muted">No matches</div>
            )}
          </ComboboxOptions>
        </div>
      </Combobox>
    </div>
  );
}
